import type { InputHTMLAttributes, ReactElement, Ref, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { Icon } from './Icon';

type Common = { label?: string; hint?: string; error?: string };

export function Field({
  label,
  hint,
  error,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & Common): ReactElement {
  const id = useId();
  return (
    <label className="field-wrap" htmlFor={id}>
      {label ? <span className="field-label">{label}</span> : null}
      <input
        id={id}
        className={`field ${className}`}
        aria-invalid={error ? 'true' : undefined}
        {...rest}
      />
      {error ? (
        <span className="field-hint error">{error}</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextArea({
  label,
  hint,
  error,
  className = '',
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & Common): ReactElement {
  const id = useId();
  return (
    <label className="field-wrap" htmlFor={id}>
      {label ? <span className="field-label">{label}</span> : null}
      <textarea
        id={id}
        className={`field ${className}`}
        aria-invalid={error ? 'true' : undefined}
        {...rest}
      />
      {error ? (
        <span className="field-hint error">{error}</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </label>
  );
}

/** A value that opens a menu says so at rest. */
export function SearchField({
  kbd = '/',
  className = '',
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  kbd?: string;
  ref?: Ref<HTMLInputElement>;
}): ReactElement {
  return (
    <div className={`search ${className}`}>
      <Icon name="search" size={16} className="search-glyph" />
      <input ref={ref} type="search" className="field" {...rest} />
      {kbd ? <span className="kbd">{kbd}</span> : null}
    </div>
  );
}

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }): ReactElement {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** A switch is only for something that takes effect immediately. */
export function Switch({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }): ReactElement {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" {...rest} />
      <span>{label}</span>
    </label>
  );
}
